import React from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    ReferenceInput,
    AutocompleteInput,
    required,
    useTranslate,
    useNotify,
    useRedirect,
    Toolbar,
    SaveButton,
} from 'react-admin';
import { Box, Card, CardContent, Typography, IconButton } from '@mui/material';
import { ArrowBack, CarRental } from '@mui/icons-material';

const transformModelSubmission = (data: any) => {
    const translations: Record<string, string> = {};
    if (data.translation_en) translations.en = data.translation_en;
    if (data.translation_zh_tw) translations['zh-TW'] = data.translation_zh_tw;
    if (data.translation_zh_cn) translations['zh-CN'] = data.translation_zh_cn;
    return { brand_id: data.brand_id, translations };
};

const CreateToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
        }}
    >
        <SaveButton
            variant="contained"
            sx={{
                backgroundColor: '#00C853',
                '&:hover': { backgroundColor: '#00A843' },
            }}
        />
    </Toolbar>
);

const PageHeader = () => {
    const translate = useTranslate();
    const redirect = useRedirect();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: { xs: 2, sm: 4 } }}>
            <IconButton
                onClick={() => redirect('/vehicle-models')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
                title={translate('ra.action.back')}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.vehicle-models.create.title', { _: 'Create Vehicle Model' })}
                </Typography>
            </Box>
        </Box>
    );
};

export const VehicleModelsCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();

    return (
        <Create
            title={false}
            component="div"
            redirect="list"
            transform={transformModelSubmission}
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
            mutationOptions={{
                onSuccess: () => {
                    notify('resources.vehicle-models.notifications.created', {
                        type: 'success',
                        messageArgs: { _: 'Model created' },
                    });
                    redirect('list', 'vehicle-models');
                },
                onError: (error: any) => {
                    const code = error?.body?.error_code as string | undefined;
                    if (code === 'VEHICLE_MODEL_DUPLICATE') {
                        notify('resources.vehicle-models.errors.duplicate', {
                            type: 'error',
                            messageArgs: { _: 'A model with this name already exists for this brand' },
                        });
                    } else if (code === 'VEHICLE_BRAND_NOT_FOUND') {
                        notify('resources.vehicle-models.errors.brandNotFound', {
                            type: 'error',
                            messageArgs: { _: 'Selected brand no longer exists' },
                        });
                    } else {
                        notify(error?.message || 'Failed to create model', { type: 'error' });
                    }
                },
            }}
        >
            <SimpleForm
                toolbar={<CreateToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <CarRental sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.vehicle-models.sections.details', {
                                        _: 'Model details',
                                    })}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <ReferenceInput
                                    source="brand_id"
                                    reference="vehicle-brands"
                                    perPage={1000}
                                >
                                    <AutocompleteInput
                                        label={translate('resources.vehicle-models.fields.brand', {
                                            _: 'Brand',
                                        })}
                                        optionText="name"
                                        validate={required()}
                                        fullWidth
                                    />
                                </ReferenceInput>
                                <TextInput
                                    source="translation_en"
                                    label={translate('resources.vehicle-models.fields.translationEn', {
                                        _: 'English Name',
                                    })}
                                    validate={required()}
                                    fullWidth
                                />
                                <TextInput
                                    source="translation_zh_tw"
                                    label={translate('resources.vehicle-models.fields.translationZhTw', {
                                        _: '繁體中文 Name',
                                    })}
                                    fullWidth
                                />
                                <TextInput
                                    source="translation_zh_cn"
                                    label={translate('resources.vehicle-models.fields.translationZhCn', {
                                        _: '简体中文 Name',
                                    })}
                                    fullWidth
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Create>
    );
};
